import { FormattedMessage } from 'react-intl'
import {
  PlanMenuHeaderActions,
  PlanMenuHeaderContainer,
  PlanMenuHeaderTitle,
  PlanMenuHeaderTitleSpan,
  PlanMenuSelectionView
} from '../styled-component'
import ButtonComponent from '../../../components/stories/atoms/button'
import { buttonVariantOptions } from '../../../components/stories/atoms/button/constants'
import {
  ImportPlan,
  NewFolder
} from '../../../components/stories/atoms/icons/entities'
import colorsOptions from '../../../components/stories/fundamentals/colors-styles/constants'
import { planPermissionOptions, viewTypeOptions } from '../constants'
import {
  Viewlist,
  ViewTale
} from '../../../components/stories/atoms/icons/core'
import { useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import ListViewButton from '../../../components/list-view-button'
import { trackFirebaseEvent } from '../../../firebase/firebase-analytics'
import { usePlanMenuListViewControl } from '../../../hooks/use-plan-menu-list-view-control'
import { usePlanMenuStore } from '../../../zustand/plan-menu/use-plan-menu-store'

const PlanMenuHeader = ({ projectDetails }) => {
  // helpers
  const history = useHistory()

  const projectName = projectDetails?.name
  const projectId = projectDetails?.id
  const permissions = projectDetails?.permissions
  const { viewType } = usePlanMenuListViewControl()

  const pageTitle = usePlanMenuStore((state) => state.pageTitle)
  const tileViewFolderHistory = usePlanMenuStore(
    (state) => state.tileViewFolderHistory
  )
  const definePageTitle = pageTitle ?? projectName

  const canCreatePlan = permissions?.includes(planPermissionOptions.CREATE_PLAN)
  const canCreateFolder = permissions?.includes(
    planPermissionOptions.CREATE_PLAN_FOLDER
  )
  const isActive = (type) => viewType === type

  // handlers
  const handleImportPlan = useCallback(() => {
    const hasTitleViewFolderHistory = tileViewFolderHistory.length > 0

    trackFirebaseEvent('plan_menu_import_plan', {
      source: 'plan_menu'
    })

    history.push(
      `/project/${projectId}/import${hasTitleViewFolderHistory ? `/${tileViewFolderHistory.at(-1)?.id}` : ''}?view=${viewType}`
    )
  }, [history, projectId, viewType, tileViewFolderHistory])

  const handleCreateFolder = useCallback(() => {
    const hasTitleViewFolderHistory = tileViewFolderHistory.length > 0

    trackFirebaseEvent('plan_menu_create_folder', {
      source: 'plan_menu'
    })

    history.push(
      `/project/${projectId}/newfolder${hasTitleViewFolderHistory ? `/${tileViewFolderHistory.at(-1)?.id}` : ''}?view=${viewType}`
    )
  }, [history, projectId, viewType, tileViewFolderHistory])

  const handleShowListView = useCallback(() => {
    history.push(`/project/${projectId}?view=${viewTypeOptions.LIST_VIEW}`)

    trackFirebaseEvent('plan_menu_view_list_selected', {
      source: 'plan_menu'
    })
  }, [history, projectId])

  const handleShowTileView = useCallback(() => {
    history.push(`/project/${projectId}?view=${viewTypeOptions.TILE_VIEW}`)

    trackFirebaseEvent('plan_menu_view_tile_selected', {
      source: 'plan_menu'
    })
  }, [history, projectId])

  // renders
  const listButtons = useMemo(
    () => [
      {
        type: viewTypeOptions.LIST_VIEW,
        icon: Viewlist,
        isLeft: true,
        onClick: handleShowListView
      },
      {
        type: viewTypeOptions.TILE_VIEW,
        icon: ViewTale,
        onClick: handleShowTileView
      }
    ],
    [handleShowListView, handleShowTileView]
  )

  return (
    <PlanMenuHeaderContainer>
      <PlanMenuHeaderTitle>
        <FormattedMessage
          id="plans_overview_header"
          values={{
            of: (chunks) => (
              <PlanMenuHeaderTitleSpan>{chunks}</PlanMenuHeaderTitleSpan>
            ),
            name: definePageTitle
          }}
        />
      </PlanMenuHeaderTitle>

      <PlanMenuHeaderActions>
        {canCreatePlan && (
          <ButtonComponent
            variant={buttonVariantOptions.PRIMARY}
            onClick={handleImportPlan}
          >
            <ImportPlan style={{ marginRight: '0.5rem' }} />
            <FormattedMessage id="plans_action_import" />
          </ButtonComponent>
        )}

        {canCreateFolder && (
          <ButtonComponent
            variant={buttonVariantOptions['TERTIARY-WHITE-FILL']}
            onClick={handleCreateFolder}
            disableIconStyle
          >
            <NewFolder
              style={{ marginRight: '0.5rem' }}
              specialStroke={colorsOptions.docuToolsOrange.orange}
            />
            <FormattedMessage id="create_folder" />
          </ButtonComponent>
        )}

        <PlanMenuSelectionView>
          {listButtons.map(({ type, icon, isLeft, onClick }) => (
            <ListViewButton
              key={type}
              Icon={icon}
              isLeft={isLeft}
              isActive={isActive(type)}
              onClick={onClick}
            />
          ))}
        </PlanMenuSelectionView>
      </PlanMenuHeaderActions>
    </PlanMenuHeaderContainer>
  )
}

export default PlanMenuHeader
